<template>
  <div class="container py-5">
    
    <div class="columns">
      <div class="column is-9 buttons">
        <router-link
          :to="{ name: 'BomMaker' }"
          class="button is-primary">
          Add a BOM
        </router-link>
        <a class="button"
          v-if="selected.length > 0"
          @click="launchConfirmDelete(true)">
          <span class="icon is-medium">
            <i class="fas fa-trash has-text-danger"></i>
          </span>
          <span>Delete {{ selected.length }} Selected BOMs</span>
        </a>
      </div>

      <div class="column is-3">
        <search-autocomplete
          :value="filters.project_id"
          @input="setFilter({ field: 'project_id', value: $event })">
        </search-autocomplete>
      </div>
    </div>

    <v-fade-transition mode="out-in">

      <article 
        v-if="!loading && boms.length == 0" 
        class="message is-info is-fullwidth">
        <div class="message-body">
          There are no BOMs.
        </div>
      </article>

      <table 
        v-if="loading || boms.length > 0"
        :key="filters.project_id"
        class="table is-fullwidth is-hoverable is-clickable">

        <thead>
          <tr>
            <th>
              <v-checkbox 
                :value="allSelected" 
                @input="toggleAllSelected($event)"/>
            </th>
            <th 
              v-for="(header, headerIndex) in headers"
              :key="headerIndex">
              {{ header }}
            </th>
            <th></th>
          </tr>
          <tr v-if="loading">
            <th :colspan="headers.length + 2" class="py-0 px-0 is-naked">
              <v-progress class="is-fullwidth" v-model="loading"/>
            </th>
          </tr>
        </thead>

        <tbody v-if="boms.length > 0">
          <tr
            class="v-centered"
            v-for="bom in boms"
            :key="bom.id"
            @click="viewBom(bom)">

            <td>
              <v-checkbox 
                @click.native.stop=""
                :value="selected.indexOf(bom.id) != -1"
                @input="toggleSelected(bom.id)"/>
            </td>
            <td>
              <span>
                {{ bom.name }}
              </span>
            </td>
            <td>
              {{ bom.project.project_name }}
            </td>
            <td>
              {{ bom.project.op_no }}
            </td>
            <td>
              {{ bom.details.length }}
            </td>
            <td>
              {{ format(parse(bom.created_on)) }}
            </td>
            <td>
              {{ bom.created_user.display_name }}
            </td>

            <td>
              <div class="buttons buttons-right">
                <button 
                  @click.stop="deleteId = bom.id; launchConfirmDelete()"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                  <span>Delete</span>
                </button>
              </div>
            </td>

          </tr>
        </tbody>

      </table>

    </v-fade-transition>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmed()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VProgress from '@/components/base/VProgress'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'

import { mapState, mapActions, mapMutations } from 'vuex'
import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VCheckbox,
    VConfirmModal, 
    VPagination,
    VProgress,
    VFadeTransition,
    SearchAutocomplete
  },
  data() {
    return {
      headers: [
        'BOM Name / Description', 'Project', 'OP Number', 
        'Number of Details', 'Created On', 'Created By'
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this BOM?',
        type: 'danger'
      },
      deleteId: null,
      bulk: false,
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      }
    }
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapState("boms", [
      "boms", "pagination", "selected", "loading", "allSelected", "filters"
    ])
  },
  watch: {
    'pagination.page'() {
      this.getBoms(this.pagination);
    },
    filters: {
      handler(filters) {
        this.getBoms(filters);
      },
      deep: true
    }
  },
  methods: {
    ...mapActions("boms", [
      "getBoms", "deleteBom", "toggleAllSelected"
    ]),
    ...mapMutations("boms", [
      "changePage", "toggleSelected", "setFilter"
    ]),
    initialize() {
      this.getBoms();
    },
    launchConfirmDelete(bulk=false) {
      this.bulk = bulk;
      if (bulk) {
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} BOMs?`;
      } else {
        this.confirmDelete.message = 'Are you sure you want to delete this BOM?'
      }
      this.confirmDelete.state = true;
    },
    deleteConfirmed() {
      this.confirmDelete.state = false;

      if (this.bulk == true) {
        this.deleteSelected();
      } else {
        this.deleteBom(this.deleteId).then(
          () => this.deleteId = null
        );
      }
    },
    deleteSelected() {
      this.selected.forEach(bomId => {
        this.deleteBom(bomId);
      })
    }, 
    viewBom(bom) {
      this.$router.push({
        name: 'BomViewer',
        params: {
          bomId: bom.id,
          bom: bom
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>